import { probeDelayMs } from './SyncBackoff';

export interface SyncTimers {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export const defaultTimers: SyncTimers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface SyncNetworkMonitorDeps {
  /** Vrai si le serveur est joignable (réponse HTTP quelconque hors 502/503/504). */
  probe: () => Promise<boolean>;
  timers?: SyncTimers;
  random?: () => number;
  now?: () => number;
  onChange: (online: boolean, since: number) => void;
}

/**
 * Détection réseau (SYNC_DESIGN §6.6). `navigator.onLine` ne suffit pas (réseau captif, pare-feu) :
 *  - hors ligne = 2 échecs consécutifs de requêtes (réseau, délai dépassé, 502-504) ou événement `offline` ;
 *  - en ligne = une sonde réussie ;
 *  - hors ligne, sonde à 5 s, 10 s, 20 s, 40 s puis toutes les 60 s (+/- 20 %) ;
 *  - l'événement `online`, le retour de focus et le bouton déclenchent une sonde immédiate.
 */
export class SyncNetworkMonitor {
  private onlineNow = true;
  private since: number;
  private consecutiveFailures = 0;
  private failedProbes = 0;
  private probeHandle: unknown = null;
  private probing = false;
  private disposed = false;
  private readonly timers: SyncTimers;
  private readonly random: () => number;
  private readonly now: () => number;

  constructor(private readonly deps: SyncNetworkMonitorDeps) {
    this.timers = deps.timers ?? defaultTimers;
    this.random = deps.random ?? Math.random;
    this.now = deps.now ?? Date.now;
    this.since = this.now();
  }

  get online(): boolean {
    return this.onlineNow;
  }

  get offlineSince(): number | null {
    return this.onlineNow ? null : this.since;
  }

  /** Une requête de synchronisation a abouti. */
  reportSuccess(): void {
    this.consecutiveFailures = 0;
    this.failedProbes = 0;
    this.cancelProbe();
    if (!this.onlineNow) this.setOnline(true);
  }

  /** Une requête a échoué faute de réseau (jamais pour un refus applicatif). Retourne vrai si on passe hors ligne. */
  reportFailure(): boolean {
    this.consecutiveFailures += 1;
    if (this.consecutiveFailures >= 2 && this.onlineNow) {
      this.setOnline(false);
      this.scheduleProbe();
      return true;
    }
    return !this.onlineNow;
  }

  /** Événement `offline` du navigateur : hors ligne tout de suite. */
  browserOffline(): void {
    this.consecutiveFailures = Math.max(this.consecutiveFailures, 2);
    if (this.onlineNow) {
      this.setOnline(false);
      this.scheduleProbe();
    }
  }

  /** Événement `online`, retour de focus ou bouton : sonde immédiate. */
  async probeNow(): Promise<boolean> {
    if (this.disposed) return false;
    this.cancelProbe();
    return this.runProbe();
  }

  dispose(): void {
    this.disposed = true;
    this.cancelProbe();
  }

  private async runProbe(): Promise<boolean> {
    if (this.probing) return this.onlineNow;
    this.probing = true;
    let ok = false;
    try {
      ok = await this.deps.probe();
    } catch {
      ok = false;
    } finally {
      this.probing = false;
    }
    if (this.disposed) return ok;
    if (ok) {
      this.reportSuccess();
    } else if (!this.onlineNow) {
      this.failedProbes += 1;
      this.scheduleProbe();
    } else {
      this.reportFailure();
    }
    return ok;
  }

  private scheduleProbe(): void {
    if (this.disposed) return;
    this.cancelProbe();
    const delay = probeDelayMs(this.failedProbes, this.random);
    this.probeHandle = this.timers.set(() => {
      this.probeHandle = null;
      void this.runProbe();
    }, delay);
  }

  private cancelProbe(): void {
    if (this.probeHandle !== null) this.timers.clear(this.probeHandle);
    this.probeHandle = null;
  }

  private setOnline(online: boolean): void {
    if (this.onlineNow === online) return;
    this.onlineNow = online;
    this.since = this.now();
    this.deps.onChange(online, this.since);
  }
}
